import { z } from "zod";
import { readOnlyReasonSchema, tenantStatusSchema } from "../auth/auth.js";
import { featureKeySchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { platformPermissionSchema, platformRoleSchema } from "./roles.js";
/**
 * Platform paneli sözleşmeleri — personel, kiracılar, özellikler, planlar, abonelik, faturalar, tahsilat, ayarlar
 * (Prompt 24, ADR 0024). Tutarlar kuruş dizgesi (KDV hariç fiyatlar), tarihler yerel gün.
 */
const versionSchema = z.number().int().positive();
const reasonSchema = z.string().trim().min(3, "En az 3 karakter").max(500);
const refSchema = z.object({ id: uuidSchema, name: z.string() });
const percentSchema = rateSchema.refine((value) => Number(value) >= 0 && Number(value) <= 100, "Yüzde 0–100 arası olmalıdır");
// ── Personel ve oturum ────────────────────────────────────────────────────────
export const platformMeSchema = z.object({
    user: z.object({ id: uuidSchema, email: z.string(), fullName: z.string(), mfaEnabled: z.boolean() }),
    role: platformRoleSchema,
    permissions: z.array(platformPermissionSchema),
    mfaRequired: z
        .boolean()
        .describe("PLATFORM_REQUIRE_MFA açık ve kullanıcıda 2FA yok → yalnızca bu uç çalışır"),
});
export const platformStaffSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    fullName: z.string(),
    role: platformRoleSchema,
    mfaEnabled: z.boolean(),
    lastLoginAt: timestampSchema.nullable(),
});
export const platformStaffListSchema = z.object({ items: z.array(platformStaffSchema) });
export const addPlatformStaffSchema = z.object({
    email: z.email("Geçerli bir e-posta girin").max(254),
    role: platformRoleSchema,
});
export const setPlatformStaffRoleSchema = z.object({
    role: platformRoleSchema.nullable().describe("null → platform yetkisi kaldırılır"),
});
// ── Abonelik durumu ───────────────────────────────────────────────────────────
export const PLATFORM_SUBSCRIPTION_STATUSES = [
    "TRIAL",
    "ACTIVE",
    "PAST_DUE",
    "SUSPENDED",
    "CANCELLED",
];
export const platformSubscriptionStatusSchema = z.enum(PLATFORM_SUBSCRIPTION_STATUSES);
export const PLATFORM_SUBSCRIPTION_STATUS_LABELS = {
    TRIAL: "Deneme",
    ACTIVE: "Etkin",
    PAST_DUE: "Ödeme gecikti",
    SUSPENDED: "Askıda (salt okunur)",
    CANCELLED: "İptal",
};
export const BILLING_PERIODS = ["MONTHLY", "YEARLY"];
export const billingPeriodSchema = z.enum(BILLING_PERIODS);
export const BILLING_PERIOD_LABELS = {
    MONTHLY: "Aylık",
    YEARLY: "Yıllık",
};
export const TENANT_STATUS_LABELS = {
    TRIAL: "Deneme",
    ACTIVE: "Etkin",
    SUSPENDED: "Askıda",
    READ_ONLY: "Salt okunur",
    CLOSED: "Kapalı",
};
// ── Kiracılar ─────────────────────────────────────────────────────────────────
export const platformTenantSummarySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    status: tenantStatusSchema,
    readOnlyReason: readOnlyReasonSchema.nullable(),
    planCode: z.string(),
    planName: z.string(),
    subscriptionStatus: platformSubscriptionStatusSchema.nullable(),
    activeStations: z.number().int(),
    members: z.number().int(),
    lastActivityAt: timestampSchema.nullable().describe("Üyelerin oturumlarındaki son kullanım"),
    openCriticalAlarms: z.number().int(),
    overdueAmount: nonNegativeMoneySchema,
    createdAt: timestampSchema,
});
export const platformTenantListQuerySchema = pageQuerySchema.extend({
    search: z.string().trim().max(80).optional(),
    status: tenantStatusSchema.optional(),
    planCode: z.string().max(40).optional(),
    subscriptionStatus: platformSubscriptionStatusSchema.optional(),
    overdue: z.enum(["true"]).optional(),
});
export const platformTenantListSchema = pageOf(platformTenantSummarySchema);
export const platformTenantDetailSchema = platformTenantSummarySchema.extend({
    statusReason: z.string().nullable(),
    statusChangedAt: timestampSchema.nullable(),
    closedAt: timestampSchema.nullable(),
    mfaRequired: z.boolean(),
    onboardingCompleted: z.boolean(),
    companies: z.array(z.object({ id: uuidSchema, legalName: z.string(), taxId: z.string(), isActive: z.boolean() })),
    owners: z.array(z.object({ id: uuidSchema, fullName: z.string(), email: z.string() })),
    pendingOwnerInvitation: z
        .string()
        .nullable()
        .describe("Hesabı olmayan sahibe gönderilen davetin e-postası"),
    allowedTransitions: z.array(tenantStatusSchema),
});
export const createPlatformTenantSchema = z.object({
    name: z.string().trim().min(2).max(200),
    planCode: z.string().trim().min(2).max(40),
    billingPeriod: billingPeriodSchema.default("MONTHLY"),
    trialDays: z.number().int().min(0).max(180).nullable().default(null).describe("Boş → planın deneme süresi"),
    ownerEmail: z.email("Geçerli bir e-posta girin").max(254),
    ownerFullName: z.string().trim().min(2).max(200),
});
export const createPlatformTenantResultSchema = z.object({
    tenantId: uuidSchema,
    ownerStatus: z.enum(["MEMBER_ADDED", "INVITED"]),
});
export const changeTenantStatusSchema = z.object({
    status: z.enum(["ACTIVE", "SUSPENDED", "READ_ONLY"]),
    reason: reasonSchema,
});
export const platformTenantStationSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    companyId: uuidSchema,
    status: z.string(),
    featureOverrides: z.array(z.object({ featureKey: featureKeySchema, enabled: z.boolean() })),
});
export const platformTenantStationsSchema = z.object({ items: z.array(platformTenantStationSchema) });
// ── Özellik bayrakları ────────────────────────────────────────────────────────
export const tenantFeatureStateSchema = z.object({
    featureKey: featureKeySchema,
    label: z.string(),
    inPlan: z.boolean(),
    rollout: z.object({ strategy: z.enum(["PERCENT", "LIST"]), included: z.boolean() }).nullable(),
    override: z
        .object({ enabled: z.boolean(), reason: z.string().nullable(), updatedAt: timestampSchema })
        .nullable(),
    effective: z.boolean(),
});
export const tenantFeaturesSchema = z.object({ items: z.array(tenantFeatureStateSchema) });
export const setTenantFeatureSchema = z.object({
    enabled: z.boolean().nullable().describe("null → geçersiz kılma kaldırılır (plan + yayın karar verir)"),
    reason: z.string().trim().max(300).nullable().default(null),
});
export const setStationFeatureSchema = z.object({
    stationId: uuidSchema,
    featureKey: featureKeySchema,
    enabled: z.boolean().nullable().describe("false → istasyonda kapalı; null → kiracı kararı"),
});
// ── Planlar ───────────────────────────────────────────────────────────────────
export const USAGE_ITEM_CODES = ["EV_SESSION", "SMS", "E_DOCUMENT"];
export const usageItemCodeSchema = z.enum(USAGE_ITEM_CODES);
export const USAGE_ITEM_LABELS = {
    EV_SESSION: "Şarj oturumu",
    SMS: "SMS",
    E_DOCUMENT: "e-Belge gönderimi",
};
export const planPriceSchema = z.object({
    id: uuidSchema,
    validFrom: localDateSchema,
    stationMonthlyPrice: nonNegativeMoneySchema,
    yearlyDiscountPercent: z.string(),
    note: z.string().nullable(),
    createdAt: timestampSchema,
});
export const planUsagePriceSchema = z.object({
    itemCode: usageItemCodeSchema,
    unitPrice: nonNegativeMoneySchema,
    includedQuantity: z.number().int().nonnegative(),
});
export const platformPlanSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    maxStations: z.number().int().nullable(),
    trialDays: z.number().int(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    features: z.array(featureKeySchema),
    currentPrice: planPriceSchema.nullable(),
    prices: z.array(planPriceSchema),
    usagePrices: z.array(planUsagePriceSchema),
    tenantCount: z.number().int(),
    version: z.number().int(),
});
export const platformPlanListSchema = z.object({ items: z.array(platformPlanSchema) });
export const createPlanSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Kod BÜYÜK harf, rakam ve alt çizgi olmalıdır"),
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(300).nullable().default(null),
    maxStations: z.number().int().positive().nullable().default(null),
    trialDays: z.number().int().min(0).max(180).default(30),
    features: z.array(featureKeySchema).min(1).max(40),
    stationMonthlyPrice: nonNegativeMoneySchema,
    yearlyDiscountPercent: percentSchema.default("0"),
    validFrom: localDateSchema,
});
export const updatePlanSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().trim().max(300).nullable().optional(),
    maxStations: z.number().int().positive().nullable().optional(),
    trialDays: z.number().int().min(0).max(180).optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
    features: z.array(featureKeySchema).min(1).max(40).optional(),
    version: versionSchema,
});
export const addPlanPriceSchema = z.object({
    validFrom: localDateSchema,
    stationMonthlyPrice: nonNegativeMoneySchema,
    yearlyDiscountPercent: percentSchema,
    note: z.string().trim().max(300).nullable().default(null),
});
export const setPlanUsagePricesSchema = z.object({
    items: z
        .array(planUsagePriceSchema)
        .max(USAGE_ITEM_CODES.length)
        .refine((items) => new Set(items.map((item) => item.itemCode)).size === items.length, "Kalem iki kez girilemez"),
});
// ── Abonelik ──────────────────────────────────────────────────────────────────
export const subscriptionDiscountSchema = z.object({
    id: uuidSchema,
    kind: z.enum(["PERCENT", "AMOUNT"]),
    percent: z.string().nullable(),
    amount: moneySchema.nullable(),
    description: z.string(),
    validFrom: localDateSchema,
    validTo: localDateSchema.nullable(),
    isActive: z.boolean(),
    createdAt: timestampSchema,
});
export const subscriptionSchema = z.object({
    tenantId: uuidSchema,
    plan: z.object({ code: z.string(), name: z.string() }),
    status: platformSubscriptionStatusSchema,
    billingPeriod: billingPeriodSchema,
    startedOn: localDateSchema,
    trialEndsOn: localDateSchema.nullable(),
    currentPeriodStart: localDateSchema,
    currentPeriodEnd: localDateSchema,
    nextPlan: z.object({ code: z.string(), name: z.string() }).nullable(),
    nextBillingPeriod: billingPeriodSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    paymentTermDays: z.number().int(),
    discounts: z.array(subscriptionDiscountSchema),
    version: z.number().int(),
});
export const changeSubscriptionSchema = z.object({
    nextPlanCode: z
        .string()
        .max(40)
        .nullable()
        .optional()
        .describe("Bir sonraki dönemden geçerli (denemede hemen)"),
    nextBillingPeriod: billingPeriodSchema.nullable().optional(),
    paymentTermDays: z.number().int().min(0).max(90).optional(),
    cancelAtPeriodEnd: z.boolean().optional(),
    endTrialNow: z.boolean().optional().describe("Denemeyi bugün bitir, abonelik etkinleşsin"),
    version: versionSchema,
});
export const addSubscriptionDiscountSchema = z
    .object({
    kind: z.enum(["PERCENT", "AMOUNT"]),
    percent: percentSchema.nullable().default(null),
    amount: moneySchema.nullable().default(null),
    description: z.string().trim().min(2).max(200),
    validFrom: localDateSchema,
    validTo: localDateSchema.nullable().default(null),
})
    .superRefine((value, context) => {
    if (value.kind === "PERCENT" && (!value.percent || Number(value.percent) <= 0))
        context.addIssue({ code: "custom", path: ["percent"], message: "Yüzde girin" });
    if (value.kind === "AMOUNT" && (!value.amount || BigInt(value.amount) <= 0n))
        context.addIssue({ code: "custom", path: ["amount"], message: "Tutar girin" });
    if (value.validTo && value.validTo < value.validFrom)
        context.addIssue({ code: "custom", path: ["validTo"], message: "Bitiş başlangıçtan önce olamaz" });
});
export const usageDaySchema = z.object({
    date: localDateSchema,
    activeStations: z.number().int(),
    activeUsers: z.number().int(),
    evSessions: z.number().int(),
    smsSent: z.number().int(),
    edocumentsSent: z.number().int(),
});
export const tenantUsageSchema = z.object({
    days: z.array(usageDaySchema),
    currentPeriod: z.object({
        start: localDateSchema,
        end: localDateSchema,
        billableStations: z.number().int(),
        evSessions: z.number().int(),
        smsSent: z.number().int(),
        edocumentsSent: z.number().int(),
    }),
    enabledFeatures: z.array(featureKeySchema),
    lastMeasuredAt: timestampSchema.nullable(),
});
export const billingProfileSchema = z.object({
    legalName: z.string(),
    taxId: z.string(),
    taxOffice: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    email: z.string(),
    linkedToBilling: z.boolean().describe("Faturalama kiracısında cari açıldı"),
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const saveBillingProfileSchema = z.object({
    legalName: z.string().trim().min(2).max(300),
    taxId: z
        .string()
        .trim()
        .regex(/^\d{10,11}$/, "VKN 10, TCKN 11 hane olmalıdır"),
    taxOffice: z.string().trim().max(120).nullable().default(null),
    address: z.string().trim().max(500).nullable().default(null),
    city: z.string().trim().max(80).nullable().default(null),
    district: z.string().trim().max(80).nullable().default(null),
    email: z.email("Geçerli bir e-posta girin").max(254),
    version: z.number().int().nonnegative().describe("Profil yoksa 0"),
});
// ── Faturalar ─────────────────────────────────────────────────────────────────
export const PLATFORM_INVOICE_STATUSES = ["ISSUED", "PAID", "VOID"];
export const platformInvoiceStatusSchema = z.enum(PLATFORM_INVOICE_STATUSES);
export const PLATFORM_INVOICE_STATUS_LABELS = {
    ISSUED: "Ödenmedi",
    PAID: "Ödendi",
    VOID: "İptal",
};
export const EDOCUMENT_LINK_STATUSES = ["PENDING", "CREATED", "FAILED", "NOT_CONFIGURED"];
export const edocumentLinkStatusSchema = z.enum(EDOCUMENT_LINK_STATUSES);
export const EDOCUMENT_LINK_LABELS = {
    PENDING: "Kesilecek",
    CREATED: "Kesildi",
    FAILED: "Hata",
    NOT_CONFIGURED: "Faturalama ayarı yok",
};
export const platformInvoiceSummarySchema = z.object({
    id: uuidSchema,
    invoiceNo: z.string(),
    tenant: refSchema,
    periodStart: localDateSchema,
    periodEnd: localDateSchema,
    issueDate: localDateSchema,
    dueDate: localDateSchema,
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
    totalAmount: nonNegativeMoneySchema,
    paidAmount: nonNegativeMoneySchema,
    outstanding: nonNegativeMoneySchema,
    status: platformInvoiceStatusSchema,
    daysOverdue: z.number().int().describe("0 = vadesi geçmedi"),
    edocumentStatus: edocumentLinkStatusSchema,
    billingInvoiceNo: z.string().nullable(),
});
export const platformInvoiceLineSchema = z.object({
    lineNo: z.number().int(),
    kind: z.enum(["SUBSCRIPTION", "USAGE"]),
    code: z.string(),
    description: z.string(),
    quantity: quantitySchema,
    unitPrice: nonNegativeMoneySchema,
    grossAmount: nonNegativeMoneySchema,
    discountAmount: nonNegativeMoneySchema,
    netAmount: nonNegativeMoneySchema,
});
export const platformInvoiceDetailSchema = platformInvoiceSummarySchema.extend({
    planCode: z.string(),
    billingPeriod: billingPeriodSchema,
    billableStations: z.number().int(),
    vatRateCode: z.string(),
    vatRate: z.string(),
    lines: z.array(platformInvoiceLineSchema),
    payments: z.array(z.object({ paymentId: uuidSchema, paidOn: localDateSchema, amount: moneySchema, reversed: z.boolean() })),
    remindersSent: z.array(z.number().int()),
    edocumentError: z.string().nullable(),
    voidReason: z.string().nullable(),
    version: z.number().int(),
});
export const platformInvoiceListQuerySchema = pageQuerySchema.extend({
    tenantId: uuidSchema.optional(),
    status: z.enum(["OPEN", "OVERDUE", "PAID", "VOID"]).optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const platformInvoiceListSchema = pageOf(platformInvoiceSummarySchema).extend({
    totals: z.object({ outstanding: nonNegativeMoneySchema, overdue: nonNegativeMoneySchema }),
});
export const billingRunResultSchema = z.object({
    invoices: z.array(z.object({ id: uuidSchema, invoiceNo: z.string(), periodStart: localDateSchema })),
    skipped: z.array(z.object({ tenantId: uuidSchema, reason: z.string() })),
});
export const billingPreviewSchema = z.object({
    periodStart: localDateSchema,
    periodEnd: localDateSchema,
    billableStations: z.number().int(),
    lines: z.array(platformInvoiceLineSchema),
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
    totalAmount: nonNegativeMoneySchema,
    vatRate: z.string(),
    note: z.string().describe("Önizlemenin kapsadığı dönem (dönem sonu gelmediyse o güne kadarki ölçüm)"),
});
export const voidPlatformInvoiceSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const retryEdocumentSchema = z.object({ version: versionSchema });
// ── Tahsilat ──────────────────────────────────────────────────────────────────
export const PAYMENT_METHODS = ["BANK_TRANSFER", "CARD", "CASH", "OTHER"];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const PAYMENT_METHOD_LABELS = {
    BANK_TRANSFER: "Havale / EFT",
    CARD: "Kredi kartı",
    CASH: "Nakit",
    OTHER: "Diğer",
};
export const platformPaymentSchema = z.object({
    id: uuidSchema,
    tenant: refSchema,
    paidOn: localDateSchema,
    amount: moneySchema,
    method: paymentMethodSchema,
    reference: z.string().nullable(),
    note: z.string().nullable(),
    unallocatedAmount: nonNegativeMoneySchema,
    status: z.enum(["RECORDED", "REVERSED"]),
    allocations: z.array(z.object({ invoiceId: uuidSchema, invoiceNo: z.string(), amount: moneySchema })),
    recordedBy: z.string().nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const platformPaymentListQuerySchema = pageQuerySchema.extend({ tenantId: uuidSchema.optional() });
export const platformPaymentListSchema = pageOf(platformPaymentSchema);
export const recordPlatformPaymentSchema = z.object({
    tenantId: uuidSchema,
    paidOn: localDateSchema,
    amount: moneySchema.refine((value) => /^[1-9]\d*$/.test(value), "Tutar sıfırdan büyük olmalıdır"),
    method: paymentMethodSchema,
    reference: z.string().trim().max(120).nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
export const reversePlatformPaymentSchema = z.object({ reason: reasonSchema, version: versionSchema });
// ── Ayarlar ───────────────────────────────────────────────────────────────────
export const platformSettingsSchema = z.object({
    billing: z.object({
        tenant: refSchema
            .nullable()
            .describe("Platform şirketinin kiracısı (kendi faturalarımız burada kesilir)"),
        company: refSchema.nullable(),
        vatRateCode: z.string().nullable(),
        configured: z.boolean(),
    }),
    dunning: z.object({ reminderDays: z.array(z.number().int()), graceDays: z.number().int() }),
    retention: z.object({ years: z.number().int() }),
});
export const savePlatformSettingsSchema = z.object({
    billing: z
        .object({ tenantId: uuidSchema, companyId: uuidSchema, vatRateCode: z.string().trim().min(1).max(40) })
        .nullable()
        .optional(),
    dunning: z
        .object({
        reminderDays: z
            .array(z.number().int().min(1).max(60))
            .max(5)
            .refine((days) => new Set(days).size === days.length, "Aynı gün iki kez girilemez"),
        graceDays: z.number().int().min(1).max(120),
    })
        .optional(),
    retention: z.object({ years: z.number().int().min(1).max(30) }).optional(),
});
//# sourceMappingURL=billing.js.map